import type { RadioProgram } from '@/mocks/adminSchedule';
import { programTypeColors, programTypeLabels } from '@/mocks/adminSchedule';

interface ProgramCardProps {
  program: RadioProgram;
  isLive?: boolean;
  compact?: boolean;
  onClick?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
  onQuickTimeChange?: () => void;
  onToggleActive?: () => void;
  onCancel?: () => void;
}

export default function ProgramCard({ program, isLive, compact, onClick, onEdit, onDelete, onDuplicate, onQuickTimeChange, onToggleActive, onCancel }: ProgramCardProps) {
  const typeColor = programTypeColors[program.type];
  const hasActions = !!(onEdit || onDelete || onDuplicate || onQuickTimeChange || onToggleActive || onCancel);

  const handleActionClick = (e: React.MouseEvent, handler?: () => void) => {
    e.stopPropagation();
    handler?.();
  };

  if (compact) {
    return (
      <div
        onClick={onClick}
        className={`px-3 py-2.5 rounded-lg transition-colors bg-background-50 border border-background-200/70 hover:border-background-300/80 ${onClick ? 'cursor-pointer' : ''} ${!program.isActive || program.cancelled ? 'opacity-60' : ''}`}
      >
        <div className="flex items-center gap-2.5">
          <div className={`w-1 h-10 rounded-full ${program.cancelled ? 'bg-red-400' : program.isActive ? typeColor.stripe.replace('border-l-', 'bg-') : 'bg-foreground-300'}`}></div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <p className="text-foreground-900 text-xs font-semibold truncate">{program.name}</p>
              {program.isLive && (
                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse shrink-0"></span>
              )}
              {program.cancelled && (
                <span className="text-[9px] font-bold text-red-600 bg-red-50 px-1.5 py-0.5 rounded shrink-0">Cancelado</span>
              )}
              {!program.isActive && !program.cancelled && (
                <span className="text-foreground-400 text-[9px] font-medium shrink-0">Inactivo</span>
              )}
            </div>
            <p className="text-foreground-500 text-[10px]">{program.startTime} - {program.endTime}</p>
          </div>
          {program.image && (
            <div className="w-10 h-10 rounded-lg overflow-hidden bg-background-200 shrink-0 hidden sm:block">
              <img src={program.image} alt={program.name} className="w-full h-full object-cover" />
            </div>
          )}
          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold ${program.cancelled ? 'bg-red-100 text-red-600' : program.isActive ? typeColor.bg + ' ' + typeColor.text : 'bg-foreground-100 text-foreground-400'} whitespace-nowrap`}>
            <i className={`${program.cancelled ? 'ri-close-circle-line' : typeColor.icon} text-[8px]`}></i>
            {program.cancelled ? 'Cancelado' : programTypeLabels[program.type]}
          </span>
          {hasActions && (
            <div className="flex items-center gap-0.5 shrink-0">
              {onToggleActive && !program.cancelled && (
                <button
                  onClick={(e) => handleActionClick(e, onToggleActive)}
                  className={`w-5 h-5 rounded-md flex items-center justify-center cursor-pointer transition-colors ${
                    program.isActive ? 'bg-emerald-50 hover:bg-emerald-100 text-emerald-600' : 'bg-foreground-100 hover:bg-foreground-200 text-foreground-400'
                  }`}
                  title={program.isActive ? 'Desactivar programa' : 'Activar programa'}
                >
                  <i className={`${program.isActive ? 'ri-toggle-line' : 'ri-toggle-fill'} text-[10px]`}></i>
                </button>
              )}
              {onQuickTimeChange && !program.cancelled && (
                <button
                  onClick={(e) => handleActionClick(e, onQuickTimeChange)}
                  className="w-5 h-5 rounded-md bg-background-100 flex items-center justify-center cursor-pointer hover:bg-sky-50 transition-colors"
                  title="Cambiar horario"
                >
                  <i className="ri-time-line text-foreground-400 text-[10px]"></i>
                </button>
              )}
              {onCancel && !program.cancelled && (
                <button
                  onClick={(e) => handleActionClick(e, onCancel)}
                  className="w-5 h-5 rounded-md bg-red-50 flex items-center justify-center cursor-pointer hover:bg-red-100 transition-colors"
                  title="Cancelar programa"
                >
                  <i className="ri-close-circle-line text-red-500 text-[10px]"></i>
                </button>
              )}
              {onDuplicate && (
                <button
                  onClick={(e) => handleActionClick(e, onDuplicate)}
                  className="w-5 h-5 rounded-md bg-background-100 flex items-center justify-center cursor-pointer hover:bg-primary-50 transition-colors"
                  title="Duplicar"
                >
                  <i className="ri-file-copy-line text-foreground-400 text-[10px]"></i>
                </button>
              )}
              {onEdit && (
                <button
                  onClick={(e) => handleActionClick(e, onEdit)}
                  className="w-5 h-5 rounded-md bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
                  title="Editar"
                >
                  <i className="ri-pencil-line text-foreground-500 text-[10px]"></i>
                </button>
              )}
              {onDelete && (
                <button
                  onClick={(e) => handleActionClick(e, onDelete)}
                  className="w-5 h-5 rounded-md bg-red-50 flex items-center justify-center cursor-pointer hover:bg-red-100 transition-colors"
                  title="Eliminar"
                >
                  <i className="ri-delete-bin-line text-red-500 text-[10px]"></i>
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={onClick}
      className={`bg-background-50 rounded-xl border overflow-hidden transition-all hover:border-background-300/80 ${
        program.cancelled ? 'border-red-200/70' : 'border-background-200/70'
      } ${
        program.isLive ? 'ring-1 ring-emerald-300' : ''
      } ${!program.isActive || program.cancelled ? 'opacity-60' : ''} ${onClick ? 'cursor-pointer' : ''}`}
    >
      {/* Program image banner */}
      {program.image && (
        <div className="w-full h-36 overflow-hidden bg-background-200">
          <img src={program.image} alt={program.name} className="w-full h-full object-cover" />
        </div>
      )}
      <div className="flex">
        {/* Color stripe */}
        <div className={`w-1.5 shrink-0 ${program.cancelled ? 'bg-red-400' : program.isActive ? typeColor.stripe : 'bg-foreground-300'}`}></div>

        <div className="flex-1 p-4">
          {/* Header */}
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-0.5">
                <h4 className="text-foreground-950 font-bold text-sm">{program.name}</h4>
                {program.isLive && (
                  <span className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[9px] font-bold">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                    EN VIVO
                  </span>
                )}
                {program.cancelled && (
                  <span className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 text-[9px] font-bold">
                    <i className="ri-close-circle-line text-[10px]"></i>
                    CANCELADO
                  </span>
                )}
                {isLive && !program.isLive && (
                  <span className="px-1.5 py-0.5 rounded-full bg-accent-100 text-accent-700 text-[9px] font-bold">AHORA</span>
                )}
              </div>
              <div className="flex items-center gap-3 text-xs text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-time-line text-[11px]"></i>
                  {program.startTime} - {program.endTime}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-user-line text-[11px]"></i>
                  {program.host}
                </span>
              </div>
            </div>

            {/* Type badge */}
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-semibold ${program.cancelled ? 'bg-red-100 text-red-600' : typeColor.bg + ' ' + typeColor.text} whitespace-nowrap shrink-0`}>
              <i className={`${program.cancelled ? 'ri-close-circle-line' : typeColor.icon} text-[10px]`}></i>
              {program.cancelled ? 'Cancelado' : programTypeLabels[program.type]}
            </span>
          </div>

          {/* Description */}
          <p className="text-foreground-600 text-xs leading-relaxed mb-3 line-clamp-2">
            {program.description}
          </p>

          {/* Host avatar row */}
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-background-200 overflow-hidden">
              <img src={program.hostAvatar} alt={program.host} className="w-full h-full object-cover" />
            </div>
            <div>
              <p className="text-foreground-700 text-[11px] font-semibold">{program.host}</p>
              {program.startDate && (
                <p className="text-foreground-400 text-[9px]">
                  <i className="ri-calendar-line text-[9px] mr-0.5"></i>
                  {new Date(program.startDate).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
              )}
            </div>

            {!program.isRecurring && (
              <span className="ml-auto px-1.5 py-0.5 rounded bg-amber-50 text-amber-600 text-[9px] font-semibold">
                Evento especial
              </span>
            )}

            {/* Admin actions */}
            {hasActions && (
              <div className="flex items-center gap-1 ml-auto">
                {onToggleActive && !program.cancelled && (
                  <button
                    onClick={(e) => handleActionClick(e, onToggleActive)}
                    className={`w-7 h-7 rounded-md flex items-center justify-center cursor-pointer transition-colors ${
                      program.isActive ? 'bg-emerald-50 hover:bg-emerald-100' : 'bg-foreground-100 hover:bg-foreground-200'
                    }`}
                    title={program.isActive ? 'Desactivar programa' : 'Activar programa'}
                  >
                    <i className={`${program.isActive ? 'ri-toggle-line text-emerald-600' : 'ri-toggle-fill text-foreground-400'} text-sm`}></i>
                  </button>
                )}
                {onQuickTimeChange && !program.cancelled && (
                  <button
                    onClick={(e) => handleActionClick(e, onQuickTimeChange)}
                    className="w-7 h-7 rounded-md bg-background-100 flex items-center justify-center cursor-pointer hover:bg-sky-50 transition-colors"
                    title="Cambiar horario"
                  >
                    <i className="ri-time-line text-foreground-500 text-sm"></i>
                  </button>
                )}
                {onCancel && !program.cancelled && (
                  <button
                    onClick={(e) => handleActionClick(e, onCancel)}
                    className="w-7 h-7 rounded-md bg-red-50 flex items-center justify-center cursor-pointer hover:bg-red-100 transition-colors"
                    title="Cancelar programa"
                  >
                    <i className="ri-close-circle-line text-red-500 text-sm"></i>
                  </button>
                )}
                {onDuplicate && (
                  <button
                    onClick={(e) => handleActionClick(e, onDuplicate)}
                    className="w-7 h-7 rounded-md bg-background-100 flex items-center justify-center cursor-pointer hover:bg-primary-50 transition-colors"
                    title="Duplicar"
                  >
                    <i className="ri-file-copy-line text-foreground-500 text-sm"></i>
                  </button>
                )}
                {onEdit && (
                  <button
                    onClick={(e) => handleActionClick(e, onEdit)}
                    className="w-7 h-7 rounded-md bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
                    title="Editar"
                  >
                    <i className="ri-pencil-line text-foreground-500 text-sm"></i>
                  </button>
                )}
                {onDelete && (
                  <button
                    onClick={(e) => handleActionClick(e, onDelete)}
                    className="w-7 h-7 rounded-md bg-red-50 flex items-center justify-center cursor-pointer hover:bg-red-100 transition-colors"
                    title="Eliminar"
                  >
                    <i className="ri-delete-bin-line text-red-500 text-sm"></i>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}